import {
  IElementID,
  Project,
  ProjectMetadata,
} from '@shared/models/project.model';
import {
  compareObjectsWithAllKeys,
  createSizeTracker,
  getId,
} from './object.helpers';
import { getLast } from './array.helpers';
import {
  getModifiedElements,
  getProjectMeta,
  getVersionById,
  isProjectsEqual,
} from './project.helpers';
import { getProposals } from './proposals/proposals.helpers';
import { getBuildingVersionById } from './recursive-element.helpers';
const TEMPORAL_CHANGE_ANIMATION_LENGTH = 1500;
const MAX_UNDO_REDO_STATES = 20;
const UNDO_REDO_SIZE_LIMIT = 20_000_000;

export const undoRedoAnimation = {
  animation: `temporal-change ${TEMPORAL_CHANGE_ANIMATION_LENGTH}ms 1`,
  '@keyframes temporal-change': {
    '0%': {
      backgroundColor: 'initial',
    },
    '50%': {
      backgroundColor: '#d5d9e8',
    },
    '100%': {
      backgroundColor: 'initial',
    },
  },
} as const;

export type UndoRedoActionType = 'undo' | 'redo';

export type IUndoRedoState = {
  project: Project;

  /**
   * To highlight the element that was reset
   */
  changedElementId?: string;

  /**
   * Building version selected while this snapshot was current. History spans versions, so
   * restoring it must move the user back to the version the change belongs to — otherwise
   * undo silently edits a version that is not on screen.
   */
  selectedVersionId?: IElementID;
};

/**
 * This state is used to decide if anything have changed or not and if we should add undo/redo state to the history
 */
export type IUndoRedoInputState = {
  project: Project;
  selectedVersionId?: IElementID;
};

export type ITemporalStates = {
  currentAction?: UndoRedoActionType;
  currentState?: IUndoRedoState;
  currentEnrichedState?: IUndoRedoState;
  pastStates: IUndoRedoState[];
  futureStates: IUndoRedoState[];
};

type MetadataChange = {
  id?: keyof ProjectMetadata;
};

/**
 * Keys compared by {@link compareObjectsWithAllKeys} must map to real metadata fields.
 */
const projectMetadataKeyFromDiff = (
  key: string,
): keyof ProjectMetadata | undefined => {
  if (key === 'design') return key;
  return undefined;
};

const projectTemporal: ITemporalStates = {
  pastStates: [],
  futureStates: [],
};

const sizeTracker = createSizeTracker<IUndoRedoState>();

/**
 * After {@link syncUndoRedoStatesAfterLocalEnrichedCommit}, {@link useInitUndoRedo} runs the same
 * transition in an effect. We must ignore those duplicate calls (reference tail-dedupe fails after undo
 * pops a different stack top).
 */
type PendingDuplicateInitEffectTransition = {
  prev: Project;
  next: Project;
  /** React 18 dev Strict Mode runs effects twice; consume both skips before clearing. */
  ignoreRemaining: number;
  /**
   * After a failed persist, the store snap-back is the reverse of the optimistic write.
   * After a successful sync the reverse is a real undo and must not be skipped.
   */
  matchReverse: boolean;
};

let pendingDuplicateInitEffectTransition:
  PendingDuplicateInitEffectTransition | undefined;

/**
 * Nested skip-enrichment PUTs in flight. {@link getUndoState} / {@link getRedoState}
 * report nothing while this is > 0 so Cmd+Z cannot race persist. Counted rather than
 * a boolean so overlapping skip-enrichment writes cannot unsuppress too early.
 */
let undoRedoInFlightPersistCount = 0;

/** Incremented when recovery deliberately invalidates every temporal frame. */
let undoRedoHistoryEpoch = 0;

export type UndoRedoHistoryCheckpoint = {
  epoch: number;
  currentAction?: UndoRedoActionType;
  currentState?: IUndoRedoState;
  currentEnrichedState?: IUndoRedoState;
  pastStates: IUndoRedoState[];
  futureStates: IUndoRedoState[];
};

/** Capture temporal state before an undo/redo persist mutates the stacks. */
export const createUndoRedoHistoryCheckpoint =
  (): UndoRedoHistoryCheckpoint => ({
    epoch: undoRedoHistoryEpoch,
    currentAction: projectTemporal.currentAction,
    currentState: projectTemporal.currentState,
    currentEnrichedState: projectTemporal.currentEnrichedState,
    pastStates: [...projectTemporal.pastStates],
    futureStates: [...projectTemporal.futureStates],
  });

/**
 * Whether this checkpoint still belongs to the live undo/redo history.
 * False after a full history reset (for example optimistic-lock recovery).
 */
export const isUndoRedoHistoryCheckpointCurrent = (
  checkpoint: UndoRedoHistoryCheckpoint,
): boolean => checkpoint.epoch === undoRedoHistoryEpoch;

/**
 * Restore a failed undo/redo attempt unless recovery reset the whole history.
 * The duplicate guard prevents the store snap-back from becoming a new edit.
 */
export const restoreUndoRedoHistoryCheckpoint = (
  checkpoint: UndoRedoHistoryCheckpoint,
  attemptedProject: Project,
  restoredProject: Project,
): boolean => {
  if (checkpoint.epoch !== undoRedoHistoryEpoch) return false;

  const historyIsUnchanged =
    projectTemporal.currentAction === checkpoint.currentAction &&
    projectTemporal.currentState === checkpoint.currentState &&
    projectTemporal.currentEnrichedState === checkpoint.currentEnrichedState &&
    projectTemporal.pastStates.length === checkpoint.pastStates.length &&
    projectTemporal.pastStates.every(
      (state, index) => state === checkpoint.pastStates[index],
    ) &&
    projectTemporal.futureStates.length === checkpoint.futureStates.length &&
    projectTemporal.futureStates.every(
      (state, index) => state === checkpoint.futureStates[index],
    );
  if (historyIsUnchanged) return true;

  projectTemporal.currentAction = checkpoint.currentAction;
  projectTemporal.currentState = checkpoint.currentState;
  projectTemporal.currentEnrichedState = checkpoint.currentEnrichedState;
  projectTemporal.pastStates = [...checkpoint.pastStates];
  projectTemporal.futureStates = [...checkpoint.futureStates];
  sizeTracker.reset();
  projectTemporal.pastStates.forEach((state) => sizeTracker.track(state));
  projectTemporal.futureStates.forEach((state) => sizeTracker.track(state));
  pendingDuplicateInitEffectTransition = {
    prev: attemptedProject,
    next: restoredProject,
    ignoreRemaining: 2,
    matchReverse: true,
  };
  notifyTemporalListeners();
  return true;
};

/**
 * True when {@link useInitUndoRedo} is replaying a transition already applied by
 * {@link syncUndoRedoStatesAfterLocalEnrichedCommit} or its revert.
 * Reverse matching is only for a failed persist (store snap-back); after a successful
 * sync the reverse is user undo and must be recorded.
 */
const isPendingDuplicateInitEffectTransition = (
  prev: Project,
  next: Project,
): boolean => {
  const duplicate = pendingDuplicateInitEffectTransition;
  if (!duplicate) return false;
  if (Object.is(duplicate.prev, prev) && Object.is(duplicate.next, next)) {
    return true;
  }
  if (!duplicate.matchReverse) return false;
  return Object.is(duplicate.prev, next) && Object.is(duplicate.next, prev);
};

type Listener = () => void;
const temporalListeners = new Set<Listener>();

const notifyTemporalListeners = () => temporalListeners.forEach((fn) => fn());

export const subscribeToUndoRedoChanges = (
  listener: Listener,
): (() => void) => {
  temporalListeners.add(listener);
  return () => temporalListeners.delete(listener);
};

/**
 * Detect which {@link ProjectMetadata} field changed between two snapshots.
 * @param metaPrev - Previous metadata
 * @param metaCurrent - Current metadata
 * @returns First differing metadata key, or empty object when unchanged / unknown key
 */
export const getMetadataChange = (
  metaPrev: ProjectMetadata,
  metaCurrent: ProjectMetadata,
): MetadataChange => {
  if (metaCurrent === metaPrev) {
    return {};
  }

  const changedSettings = compareObjectsWithAllKeys(metaPrev, metaCurrent);

  if (!changedSettings || changedSettings.length === 0) {
    return {};
  }

  const id = projectMetadataKeyFromDiff(changedSettings[0] ?? '');
  if (id === undefined) return {};

  return { id };
};

export const getRedoState = (): IUndoRedoState | undefined => {
  if (undoRedoInFlightPersistCount > 0) return undefined;
  return projectTemporal.futureStates?.[0];
};

/**
 * True while at least one skip-enrichment persist has recorded an undo frame
 * but its PUT has not settled. Keyboard undo should no-op (no "Nothing to undo" toast).
 */
export const isUndoRedoSuppressedForInFlightPersist = (): boolean =>
  undoRedoInFlightPersistCount > 0;

/**
 * Nest or unwind hide-undo for an in-flight skip-enrichment persist.
 * `true` increments, `false` decrements (not below 0). Listeners fire only when
 * suppression crosses on/off, so overlapping PUTs stay hidden until the last one settles.
 */
export const setUndoRedoSuppressedForInFlightPersist = (
  suppressed: boolean,
): void => {
  const wasSuppressed = undoRedoInFlightPersistCount > 0;
  if (suppressed) {
    undoRedoInFlightPersistCount += 1;
  } else if (undoRedoInFlightPersistCount > 0) {
    undoRedoInFlightPersistCount -= 1;
  }
  const isSuppressed = undoRedoInFlightPersistCount > 0;
  if (wasSuppressed === isSuppressed) return;
  notifyTemporalListeners();
};

const getStackUndoState = (): IUndoRedoState | undefined =>
  getLast(projectTemporal.pastStates);

const getLatestEnrichedPastState = (): IUndoRedoState | undefined => {
  for (let index = projectTemporal.pastStates.length - 1; index >= 0; index--) {
    const state = projectTemporal.pastStates[index];
    if (state?.project.enriched) return state;
  }
};

/**
 * Latest undo frame for {@link currentProject}, or `undefined` while skip-enrichment
 * persist is in flight ({@link isUndoRedoSuppressedForInFlightPersist}).
 */
export const getUndoState = (
  currentProject?: Project,
): IUndoRedoState | undefined => {
  if (undoRedoInFlightPersistCount > 0) return undefined;
  const stackUndoState = getStackUndoState();
  if (!currentProject || currentProject.enriched) return stackUndoState;
  return getLatestEnrichedPastState() ?? projectTemporal.currentEnrichedState;
};

const removeLatestUndoState = (): IUndoRedoState | undefined => {
  const state = projectTemporal.pastStates.pop();
  if (state) sizeTracker.untrack(state);
  return state;
};

const removeLatestRedoState = (): IUndoRedoState | undefined => {
  const state = projectTemporal.futureStates.shift();
  if (state) sizeTracker.untrack(state);
  return state;
};

const addUndoState = (state: IUndoRedoState) => {
  const { futureStates, pastStates } = projectTemporal;
  const length = pastStates.length + futureStates.length;

  if (
    sizeTracker.total >= UNDO_REDO_SIZE_LIMIT ||
    length >= MAX_UNDO_REDO_STATES
  ) {
    const removed = pastStates.shift();
    if (removed) sizeTracker.untrack(removed);
  }
  sizeTracker.track(state);
  const result = pastStates.push(state);
  notifyTemporalListeners();
  return result;
};

const addRedoState = (state: IUndoRedoState): void => {
  const { futureStates, pastStates } = projectTemporal;
  const length = pastStates.length + futureStates.length;

  if (
    sizeTracker.total >= UNDO_REDO_SIZE_LIMIT ||
    length >= MAX_UNDO_REDO_STATES
  ) {
    const removed = futureStates.pop();
    if (removed) sizeTracker.untrack(removed);
  }
  sizeTracker.track(state);
  futureStates.unshift(state);
  notifyTemporalListeners();
};

const getActionType = (
  updatedProject: Project,
): UndoRedoActionType | undefined => {
  const undoState = getStackUndoState();
  const redoState = getRedoState();

  if (undoState && isProjectsEqual(undoState.project, updatedProject)) {
    return 'undo';
  }
  if (redoState && isProjectsEqual(redoState.project, updatedProject)) {
    return 'redo';
  }
};

const updateCurrentEnrichedState = (state: IUndoRedoState): void => {
  if (!state.project.enriched) return;
  projectTemporal.currentEnrichedState = state;
};

export const setCurrentEnrichedUndoRedoState = (
  project: Project,
  selectedVersionId?: IElementID,
): void => updateCurrentEnrichedState({ project, selectedVersionId });

/**
 * Applies temporal bookkeeping when the store was already updated with an enriched snapshot
 * (optimistic enrich path or {@link skipEnrichment}). Registers a duplicate guard so
 * {@link useInitUndoRedo}'s effect does not apply the same transition twice.
 *
 * @param prevSnapshot - Project before the local commit
 * @param nextSnapshot - Project after the local commit
 * @param selectedVersionId - Active building version id for temporal scoping
 */
export const syncUndoRedoStatesAfterLocalEnrichedCommit = (
  prevSnapshot: Project,
  nextSnapshot: Project,
  selectedVersionId: IElementID | undefined,
): void => {
  updateUndoRedoStates(
    { project: prevSnapshot, selectedVersionId },
    { project: nextSnapshot, selectedVersionId },
  );
  pendingDuplicateInitEffectTransition = {
    prev: prevSnapshot,
    next: nextSnapshot,
    ignoreRemaining: 2,
    matchReverse: false,
  };
};

/**
 * Drops the undo frame from {@link syncUndoRedoStatesAfterLocalEnrichedCommit} when that
 * local write is reverted (skip-enrichment PUT failure). Does not push a redo — persist
 * never succeeded. Guards {@link useInitUndoRedo} so the store snap-back is not recorded
 * as a new edit (or as an undo that would leave the failed snapshot on redo).
 *
 * @param prevSnapshot - Project restored into the store
 * @param nextSnapshot - Optimistic snapshot being discarded
 */
export const revertUndoRedoStatesAfterFailedEnrichedCommit = (
  prevSnapshot: Project,
  nextSnapshot: Project,
): void => {
  // Capture before pop — the frame holds the version that was on screen before the write.
  const tail = getLast(projectTemporal.pastStates);
  const selectedVersionId =
    tail?.project === prevSnapshot
      ? tail.selectedVersionId
      : projectTemporal.currentEnrichedState?.selectedVersionId;
  if (tail?.project === prevSnapshot) {
    removeLatestUndoState();
  }
  updateCurrentEnrichedState({ project: prevSnapshot, selectedVersionId });
  pendingDuplicateInitEffectTransition = {
    prev: nextSnapshot,
    next: prevSnapshot,
    ignoreRemaining: 2,
    matchReverse: true,
  };
  notifyTemporalListeners();
};

/**
 * Highlighting a changed element requires the same building version in both snapshots.
 * Switching version, adding or deleting one keeps the history (snapshots hold the whole
 * project) but leaves no shared element scope to diff.
 *
 * @param prev - State before the transition
 * @param next - State after the transition
 * @returns Version id present in both snapshots, otherwise `undefined`
 */
const getComparableVersionId = (
  prev: IUndoRedoInputState,
  next: IUndoRedoInputState,
): IElementID | undefined => {
  const versionId = next.selectedVersionId;
  if (!versionId || versionId !== prev.selectedVersionId) return undefined;
  if (!getBuildingVersionById(prev.project, versionId)) return undefined;
  return getBuildingVersionById(next.project, versionId)
    ? versionId
    : undefined;
};

/**
 * Store new undo/redo states.
 *
 * History survives a version switch: every state holds the whole {@link Project}, so an
 * added or deleted {@link Baseline} stays undoable even though the selection moved
 * with it. Only a different project resets the history. Each frame records the version that
 * was selected while it was current ({@link IUndoRedoState.selectedVersionId}) so restoring
 * it can bring the user back there.
 *
 * @param prev - State before the transition
 * @param next - State after the transition
 */
export const updateUndoRedoStates = (
  prev: IUndoRedoInputState,
  next: IUndoRedoInputState,
): void => {
  const duplicate = pendingDuplicateInitEffectTransition;
  if (isPendingDuplicateInitEffectTransition(prev.project, next.project)) {
    if (duplicate) {
      duplicate.ignoreRemaining -= 1;
      if (duplicate.ignoreRemaining <= 0) {
        pendingDuplicateInitEffectTransition = undefined;
      }
    }
    return;
  }

  if (duplicate) pendingDuplicateInitEffectTransition = undefined;

  const nextProjectId = next.project.id;
  const prevProjectId = prev.project.id;
  const currentEnriched = {
    project: next.project,
    selectedVersionId: next.selectedVersionId,
  };

  // Reset history if the project has changed
  if (nextProjectId !== prevProjectId) {
    resetUndoRedoHistory();
    return updateCurrentEnrichedState(currentEnriched);
  }

  // No changes at all
  if (isProjectsEqual(prev.project, next.project)) {
    updateCurrentEnrichedState(currentEnriched);
    return;
  }
  const actionType = getActionType(next.project);
  const comparableVersionId = getComparableVersionId(prev, next);
  const changedElementId = comparableVersionId
    ? getRecentlyUndoRedoElementId(
        prev.project,
        next.project,
        comparableVersionId,
      )
    : undefined;

  // The frame restores `prev`, so it carries the version that was on screen back then
  const newState: IUndoRedoState = {
    project: prev.project,
    changedElementId,
    selectedVersionId: prev.selectedVersionId,
  };
  const currentEnrichedWithChange = { ...currentEnriched, changedElementId };

  // If the project was restored by an undo
  if (actionType === 'undo') {
    removeLatestUndoState();
    updateCurrentEnrichedState(currentEnrichedWithChange);
    return addRedoState(newState);
  }
  // Restored by a redo
  if (actionType === 'redo') {
    removeLatestRedoState();
    updateCurrentEnrichedState(currentEnrichedWithChange);
  }

  if (!newState.project.enriched) return;
  updateCurrentEnrichedState(currentEnrichedWithChange);
  // useUpdateProject syncs via {@link syncUndoRedoStatesAfterLocalEnrichedCommit}; useInitUndoRedo effect
  // replays the same transition — duplicate calls are skipped (tail reference dedupe alone fails after undo pop).
  const tail = getLast(projectTemporal.pastStates);
  if (
    tail &&
    tail.project === newState.project &&
    tail.changedElementId === newState.changedElementId
  ) {
    return;
  }
  // Same baseline as optimistic→enriched (changedElementId can differ); avoid duplicate past frame.
  if (tail && isProjectsEqual(tail.project, newState.project)) return;
  addUndoState(newState);
};

/**
 * Get an elementId or the id of the dom-element in settings panel.
 * Used to highlight the element that was changed by undo/redo
 * @param prevProject
 * @param nextProject
 * @param selectedVersionId
 * @returns
 */
const getRecentlyUndoRedoElementId = (
  prevProject: Project,
  nextProject: Project,
  selectedVersionId: IElementID,
): string | undefined => {
  const updatedVersion = getVersionById(nextProject, selectedVersionId);

  const prevVersion = getVersionById(prevProject, selectedVersionId, false);

  if (!prevVersion) return undefined; // on first run, prevVersion is never defined

  const metaChange = getMetadataChange(
    getProjectMeta(prevProject),
    getProjectMeta(nextProject),
  );

  if (metaChange.id) {
    return metaChange.id;
  }

  // Proposal add/remove (baseline tree often unchanged; avoid picking unrelated element diffs)
  const prevProposalIds = new Set(getProposals(prevVersion).map(getId));
  const nextProposalIds = new Set(getProposals(updatedVersion).map(getId));
  const removedProposal = getProposals(prevVersion).find(
    (p) => !nextProposalIds.has(getId(p)),
  );
  const addedProposal = getProposals(updatedVersion).find(
    (p) => !prevProposalIds.has(getId(p)),
  );
  if (removedProposal && !addedProposal)
    return `proposal:${getId(removedProposal)}`;
  if (addedProposal && !removedProposal)
    return `proposal:${getId(addedProposal)}`;

  // Metadata changes triggers a lot of elements to change so ignore them
  const modifiedIds = getModifiedElements(updatedVersion, prevVersion).map(
    getId,
  );

  return modifiedIds[0];
};

export const resetUndoRedoHistory = (): void => {
  undoRedoHistoryEpoch += 1;
  projectTemporal.currentEnrichedState = undefined;
  projectTemporal.pastStates = [];
  projectTemporal.futureStates = [];
  pendingDuplicateInitEffectTransition = undefined;
  undoRedoInFlightPersistCount = 0;
  sizeTracker.reset();
  notifyTemporalListeners();
};
